import { z } from "zod";
import {
  EventTypeEnum,
  EventScoringType,
  GimmeDistance,
  HandicapMode,
  HandicapPercentage,
  EventMaxPerHoleType,
  EventRoundSkillLevel,
  WindDirection,
  PinType,
  Sky,
  Ground,
  TeeType,
  EventsWindSpeed,
} from "types/enums";
import { DateTime } from "luxon";

type InternalCreatePrivateTourDto = {
  name: string;
  allowDuplicateNames?: boolean;
};

export const CreatePrivateTourDtoSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(50, "Name must not exceed 50 characters"),
  allowDuplicateNames: z.boolean().default(false).optional(),
});

export type CreatePrivateTourDto = z.infer<typeof CreatePrivateTourDtoSchema>;

// Array schema for validation of multiple tour objects
export const CreatePrivateTourDtoArraySchema = z.array(
  CreatePrivateTourDtoSchema,
) satisfies z.ZodType<InternalCreatePrivateTourDto[]>;

// CreateEventPlayControlDto Schema
export const CreateEventPlayControlDtoSchema = z.object({
  id: z.string().optional(),
  eventId: z.string().optional(),
  gimmes: z.enum(GimmeDistance).default(GimmeDistance.OFF),
  mulligans: z.boolean().default(false),
  addStrokes: z.boolean().default(false),
  removeStrokes: z.boolean().default(false),
  applyHandicaps: z.boolean().default(false),
  handicapMode: z.enum(HandicapMode).default(HandicapMode.OFF),
  handicapLevel: z.enum(HandicapPercentage).default(HandicapPercentage["100%"]),
  handicapCourseSlope: z.boolean().default(false),
  userSwingProfile: z.boolean().default(false),
  maxPerHole: z.enum(EventMaxPerHoleType).default(EventMaxPerHoleType.NONE),
});

export const UpdateEventPlayControlDtoSchema = z.object({
  id: z.string().optional(),
  eventId: z.string().optional(),
  gimmes: z.enum(GimmeDistance).default(GimmeDistance.OFF).optional(),
  mulligans: z.boolean().default(false).optional(),
  addStrokes: z.boolean().default(false).optional(),
  removeStrokes: z.boolean().default(false).optional(),
  applyHandicaps: z.boolean().default(false).optional(),
  handicapMode: z.enum(HandicapMode).default(HandicapMode.OFF).optional(),
  handicapLevel: z
    .enum(HandicapPercentage)
    .default(HandicapPercentage["100%"])
    .optional(),
  handicapCourseSlope: z.boolean().default(false).optional(),
  userSwingProfile: z.boolean().default(false).optional(),
  maxPerHole: z
    .enum(EventMaxPerHoleType)
    .default(EventMaxPerHoleType.NONE)
    .optional(),
});

export type CreateEventPlayControlDto = z.infer<
  typeof CreateEventPlayControlDtoSchema
>;

// CreateEventRoundInfoDto Schema
export const CreateEventRoundInfoDtoSchema = z.object({
  eventId: z.string().optional(),
  id: z.string().optional(),
  // Forms / merged state can supply courseId as a string (e.g. select value)
  courseId: z.preprocess((v) => {
    if (typeof v === "string") {
      const n = parseInt(v.trim(), 10);
      return Number.isFinite(n) ? n : v;
    }
    return v;
  }, z.number().int().positive("Course ID must be a positive integer")),
  roundNumber: z.number().int().positive().default(1),
  skillLevel: z
    .enum(EventRoundSkillLevel)
    .default(EventRoundSkillLevel.AVERAGE_PRO),
  windSpeed: z.enum(EventsWindSpeed).default(EventsWindSpeed.CALM),
  windDirection: z.enum(WindDirection).default(WindDirection.N),
  holes: z
    .array(z.number().int().min(1).max(18))
    .min(1, "At least one hole must be specified")
    .max(18, "Cannot have more than 18 holes")
    .refine((holes) => new Set(holes).size === holes.length, {
      error: "Holes cannot contain duplicates",
    }),
  pin: z.enum(PinType).default(PinType.NOVICE),
  sky: z.enum(Sky).default(Sky.CLEAR),
  ground: z.enum(Ground).default(Ground.NORMAL),
  mensTees: z.enum(TeeType).default(TeeType.BLUE),
  womensTees: z.enum(TeeType).default(TeeType.RED),
});

export const UpdateEventRoundInfoDtoSchema = z.object({
  id: z.string().optional(),
  eventId: z.string().optional(),
  courseId: z
    .number()
    .int()
    .positive("Course ID must be a positive integer")
    .optional(),
  roundNumber: z.number().int().positive().default(1).optional(),
  skillLevel: z
    .enum(EventRoundSkillLevel)
    .default(EventRoundSkillLevel.AVERAGE_PRO)
    .optional(),
  windSpeed: z.enum(EventsWindSpeed).default(EventsWindSpeed.CALM).optional(),
  windDirection: z.enum(WindDirection).default(WindDirection.N).optional(),
  holes: z
    .array(z.number().int().min(1).max(18))
    .min(1, "At least one hole must be specified")
    .max(18, "Cannot have more than 18 holes")
    .refine((holes) => new Set(holes).size === holes.length, {
      error: "Holes cannot contain duplicates",
    })
    .optional(),
  pin: z.enum(PinType).default(PinType.NOVICE).optional(),
  sky: z.enum(Sky).default(Sky.CLEAR).optional(),
  ground: z.enum(Ground).default(Ground.NORMAL).optional(),
  mensTees: z.enum(TeeType).default(TeeType.BLUE).optional(),
  womensTees: z.enum(TeeType).default(TeeType.RED).optional(),
});

export type CreateEventRoundInfoDto = z.infer<
  typeof CreateEventRoundInfoDtoSchema
>;
const normalizeToIsoWithZone = (
  input: string | Date,
  zone?: string | null,
): string => {
  const dt =
    input instanceof Date
      ? DateTime.fromJSDate(input)
      : DateTime.fromISO(input);

  const effectiveZone = zone && zone.trim().length > 0 ? zone.trim() : "local";

  const zoned = dt.setZone(effectiveZone, { keepLocalTime: false });

  const iso = zoned.toISO(); // string | null
  if (!iso) {
    throw new Error("Invalid datetime value");
  }

  return iso;
};

const BaseCreateEventDtoSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(50, "Name must not exceed 50 characters")
    .refine((name) => !/[/\\.]/g.test(name), {
      error:
        "Name cannot contain forward slash (/), backslash (\\), or period (.)",
    }),

  allowDuplicateNames: z.boolean().default(false),

  description: z
    .string()
    .max(110, "Description must not exceed 110 characters")
    .optional()
    .nullable(),

  // Required by the API on create, and spelled with a lowercase
  // 'z' on the wire — any other casing is dropped by the parse.
  eventTimezone: z.string().optional().nullable(),

  // raw inputs: ISO string or Date
  startTime: z.union([
    z.iso.datetime({
      message: "Start time must be a valid ISO datetime string",
    }),
    z.date(),
  ]),

  endTime: z.union([
    z.iso.datetime({ message: "End time must be a valid ISO datetime string" }),
    z.date(),
  ]),

  eventType: z.enum(EventTypeEnum),
  numberOfRounds: z.number().int().nonnegative().default(1),
  replays: z.number().int().nonnegative().default(1),
  maxUserCount: z.number().int().nonnegative().default(0),
  scoringType: z.enum(EventScoringType),

  controls: CreateEventPlayControlDtoSchema.default(() =>
    CreateEventPlayControlDtoSchema.parse({}),
  ),

  rounds: z.array(CreateEventRoundInfoDtoSchema).default([]),
});

// CreateEventDto Schema
export const CreateEventDtoSchema = BaseCreateEventDtoSchema.transform(
  (data) => {
    const { eventTimezone } = data;

    const startTimeIso = normalizeToIsoWithZone(data.startTime, eventTimezone);
    const endTimeIso = normalizeToIsoWithZone(data.endTime, eventTimezone);

    return {
      ...data,
      startTime: startTimeIso,
      endTime: endTimeIso,
    };
  },
).superRefine((data, ctx) => {
  // Validate that startTime is before endTime
  // Convert ISO strings back to DateTime objects for comparison
  const zone =
    data.eventTimezone && data.eventTimezone.trim().length > 0
      ? data.eventTimezone.trim()
      : "local";

  const startDt = DateTime.fromISO(data.startTime, {
    zone,
    setZone: true,
  });
  const endDt = DateTime.fromISO(data.endTime, {
    zone,
    setZone: true,
  });

  if (startDt >= endDt) {
    ctx.addIssue({
      code: "custom",
      message: "Start time must be before end time",
      path: ["startTime", "endTime"],
      input: data,
    });
  }

  // Validate that numberOfRounds matches the actual number of rounds provided
  if (
    data.numberOfRounds > 0 &&
    data.rounds.length > 0 &&
    data.numberOfRounds !== data.rounds.length
  ) {
    ctx.addIssue({
      code: "custom",
      message: `Number of rounds (${data.numberOfRounds}) must match the actual number of rounds provided (${data.rounds.length})`,
      path: ["numberOfRounds"],
      input: data,
    });
  }

  // Validate event type and scoring type consistency
  if (
    data.eventType === EventTypeEnum.LONG_DRIVE &&
    data.scoringType !== EventScoringType.LONG_DRIVE
  ) {
    ctx.addIssue({
      code: "custom",
      message: "Long drive events must use long drive scoring type",
      path: ["scoringType"],
      input: data,
    });
  }

  if (
    data.eventType === EventTypeEnum.CLOSEST_TO_PIN &&
    data.scoringType !== EventScoringType.CLOSEST_TO_PIN
  ) {
    ctx.addIssue({
      code: "custom",
      message: "Closest to pin events must use closest to pin scoring type",
      path: ["scoringType"],
      input: data,
    });
  }
});

export type CreateEventDto = z.infer<typeof CreateEventDtoSchema>;

// Array schema for validation of multiple event objects
export const CreateEventDtoArraySchema = z.array(
  CreateEventDtoSchema,
) satisfies z.ZodType<CreateEventDto[]>;

const BaseUpdateEventDtoSchema = z.object({
  id: z.string(),
  name: z
    .string()
    .min(1, "Name is required")
    .max(50, "Name must not exceed 50 characters")
    .refine((name) => !/[/\\.]/g.test(name), {
      error:
        "Name cannot contain forward slash (/), backslash (\\), or period (.)",
    })
    .optional(),
  // Optional (no default) so a PATCH only carries fields the caller sends —
  // a default would inject a value the user never changed.
  allowDuplicateNames: z.boolean().optional(),
  imageUrl: z.string().optional(),
  description: z
    .string()
    .max(110, "Description must not exceed 110 characters")
    .optional()
    .nullable(),
  startTime: z.union([z.iso.datetime(), z.date()]).optional(),
  endTime: z.union([z.iso.datetime(), z.date()]).optional(),
  // Optional on update: only sent alongside a date change (it normalizes the
  // start/end). Omitting it on a dates-unchanged PATCH lets the backend keep
  // the event's stored timezone rather than overwriting it with the editor's.
  eventTimezone: z.string().optional(),
  eventType: z.enum(EventTypeEnum).optional(),
  numberOfRounds: z.number().int().nonnegative().default(1).optional(),
  replays: z.number().int().nonnegative().default(1).optional(),
  maxUserCount: z.number().int().nonnegative().optional(),
  scoringType: z.enum(EventScoringType).optional(),
  controls: UpdateEventPlayControlDtoSchema.default({}).optional(),
  rounds: z.array(UpdateEventRoundInfoDtoSchema).default([]).optional(),
});

export const UpdateEventDtoSchema = BaseUpdateEventDtoSchema.transform(
  (data) => {
    const { eventTimezone } = data;

    let startTimeIso: string | undefined;
    if (data.startTime) {
      startTimeIso = normalizeToIsoWithZone(
        data.startTime,
        eventTimezone ?? "local",
      );
    }
    let endTimeIso: string | undefined;
    if (data.endTime) {
      endTimeIso = normalizeToIsoWithZone(
        data.endTime,
        eventTimezone ?? "local",
      );
    }

    return {
      ...data,
      startTime: startTimeIso,
      endTime: endTimeIso,
    };
  },
)

  .superRefine((data, ctx) => {
    if (data.startTime && data.endTime) {
      const startDt = DateTime.fromISO(data.startTime);
      const endDt = DateTime.fromISO(data.endTime);

      if (startDt >= endDt) {
        ctx.addIssue({
          code: "custom",
          message: "Start time must be before end time",
          path: ["startTime", "endTime"],
          input: data,
        });
      }
    }
    if (
      data.numberOfRounds &&
      data.rounds &&
      data.numberOfRounds > 0 &&
      data.rounds?.length > 0 &&
      data.numberOfRounds !== data.rounds?.length
    ) {
      ctx.addIssue({
        code: "custom",
        message: `Number of rounds (${data.numberOfRounds}) must match the actual number of rounds provided (${data.rounds.length})`,
        path: ["numberOfRounds"],
        input: data,
      });
    }

    if (
      data.eventType === EventTypeEnum.LONG_DRIVE &&
      data.scoringType !== EventScoringType.LONG_DRIVE
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Long drive events must use long drive scoring type",
        path: ["scoringType"],
        input: data,
      });
    }

    if (
      data.eventType === EventTypeEnum.CLOSEST_TO_PIN &&
      data.scoringType !== EventScoringType.CLOSEST_TO_PIN
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Closest to pin events must use closest to pin scoring type",
        path: ["scoringType"],
        input: data,
      });
    }
  });

export type UpdateEventDto = z.infer<typeof UpdateEventDtoSchema>;

// Array schema for validation of multiple event objects
export const UpdateEventDtoArraySchema = z.array(
  UpdateEventDtoSchema,
) satisfies z.ZodType<UpdateEventDto[]>;
